import { IDocProps } from '@interface';
import { ReactComponent as ArrowIcon } from '@assets/icons/arrow_grey_up_right_icon.svg';
import StatusChip from '@components/widgets/StatusChip';
import {
  INSPECTION_SECURITY_CHECK_LABEL_STATUS,
  SECURITY_CHECK_LIST,
  RULE_TYPE_MAP,
  InspectionStatusLabelEnum
} from '@constants';
import {
  InspectionSecurityStatusEnum,
  AssetType,
  InspectionStatusEnum,
  REGO_CERTIFICATE_DATA_EDIT_TYPE,
  BANK_STATEMENT_DATA_EDIT_TYPE
} from '@types';
import { getAssetSpecificContent } from '@utils';
import clsx from 'clsx';
import { useInspectionDetailStore } from '@stores';
import {
  openSecurityCheckDetail,
  getVariant,
  getLabel,
  getInspectionCheckDocument,
  resolveSecurityCheckStatus,
  resolveVehicleDataEditsStatus,
  isVisibleSecurityCheck,
  findMatchingFailedRule,
  isAiPhotoSecurityCheck,
  SECURITY_CHECK_PHOTO_DOCUMENT_TYPES
} from '../utils/securityCheckHandlers';
import { securityCheckTypeMappings } from '../utils/ruleMappings';

const Variants = {
  success_light:
    'bg-success-50 border-success-500 active:bg-success-800 hover:bg-success-800',
  info_light:
    'border-grey-300 bg-grey-25 hover:bg-grey-100 focus:bg-grey-100 active:bg-grey-200',
  warning_light:
    'border-warning-500 bg-warning-50 active:bg-warning-700 hover:bg-warning-700',
  error_light:
    'border-error-400 bg-error-50 active:!bg-error-100 hover:bg-error-100',
  none_light:
    'border-grey-300 bg-grey-50 hover:bg-grey-100 focus:bg-grey-100 active:bg-grey-200'
};

/** Statuses that do not make the row clickable (no detail panel). */
const NON_CLICKABLE_STATUSES = [
  InspectionSecurityStatusEnum.notPerformed,
  InspectionSecurityStatusEnum.pass,
  InspectionSecurityStatusEnum.noRegister,
  InspectionSecurityStatusEnum.noReport,
  InspectionSecurityStatusEnum.noRecord,
  InspectionSecurityStatusEnum.notFound,
  InspectionSecurityStatusEnum.match,
  InspectionSecurityStatusEnum.error,
  InspectionSecurityStatusEnum.notRequested
];

/** Statuses that have no detail content to show (used for "Details" link visibility). */
const STATUSES_WITHOUT_DETAILS = [
  InspectionSecurityStatusEnum.notPerformed,
  InspectionSecurityStatusEnum.pass,
  InspectionSecurityStatusEnum.noRegister,
  InspectionSecurityStatusEnum.noReport,
  InspectionSecurityStatusEnum.noRecord,
  InspectionSecurityStatusEnum.notFound,
  InspectionSecurityStatusEnum.error
];

/** Statuses that mean the check is unavailable or not performed (row not clickable, no detail panel). */
const UNAVAILABLE_OR_NOT_PERFORMED_STATUSES = [
  InspectionSecurityStatusEnum.notAvailable,
  InspectionSecurityStatusEnum.notPerformed
] as const;

function isStatusUnavailableOrNotPerformed(status: string): boolean {
  return UNAVAILABLE_OR_NOT_PERFORMED_STATUSES.includes(
    status as (typeof UNAVAILABLE_OR_NOT_PERFORMED_STATUSES)[number]
  );
}

/**
 * Returns true when the "Details" link (with arrow) should be shown for this row.
 * The link opens the detail panel; we hide it when there is nothing meaningful to show.
 */
export function shouldShowDetailsLink(
  status: string,
  key: string,
  type: string,
  documentObj: any
): boolean {
  if (status === InspectionSecurityStatusEnum.notProvided) return false;
  if (
    status === InspectionSecurityStatusEnum.notRequested &&
    (key === 'ppsr' || key === 'rav')
  ) {
    return false;
  }
  if (
    key === 'BANK_STATEMENT_ADDRESS' &&
    status === InspectionSecurityStatusEnum.warning
  ) {
    return false;
  }

  const hasActionableStatus =
    status &&
    type !== 'VEHICLE_VIN' &&
    !STATUSES_WITHOUT_DETAILS.includes(status as InspectionSecurityStatusEnum);

  const isLicenseAuthenticityWithStatus =
    key === 'licenseAuthenticity' &&
    [
      InspectionSecurityStatusEnum.pass,
      InspectionSecurityStatusEnum.failed,
      InspectionSecurityStatusEnum.warning
    ].includes(status as InspectionSecurityStatusEnum);

  const isNevdisRegistrationWithExpiry =
    type === 'NEVDIS_REGISTRATION' &&
    (status === InspectionSecurityStatusEnum.noRegister ||
      status === InspectionSecurityStatusEnum.discrepancyFound) &&
    documentObj?.expiry &&
    documentObj.expiry !== '';

  const isVehicleVinDiscrepancy =
    type === 'VEHICLE_VIN' &&
    status === InspectionSecurityStatusEnum.discrepancyFound;

  const isRegoExpiryFailed =
    type === 'REGO_EXPIRY' &&
    status === InspectionSecurityStatusEnum.failed;

  return (
    hasActionableStatus ||
    isLicenseAuthenticityWithStatus ||
    isNevdisRegistrationWithExpiry ||
    isVehicleVinDiscrepancy ||
    isRegoExpiryFailed
  );
}

export function isRowClickable(
  status: string,
  key: string,
  type: string,
  documentObj: any
): boolean {
  if (isStatusUnavailableOrNotPerformed(status)) {
    return false;
  }
  if (status && !NON_CLICKABLE_STATUSES.includes(status as InspectionSecurityStatusEnum)) {
    return true;
  }
  if (key === 'licenseAuthenticity' && status === InspectionSecurityStatusEnum.pass) {
    return true;
  }
  if (
    type === 'NEVDIS_REGISTRATION' &&
    (status === InspectionSecurityStatusEnum.noRegister ||
      status === InspectionSecurityStatusEnum.discrepancyFound) &&
    documentObj?.expiry &&
    documentObj.expiry !== ''
  ) {
    return true;
  }
  if (
    type === 'REGO_EXPIRY' &&
    [InspectionSecurityStatusEnum.pass, InspectionSecurityStatusEnum.failed].includes(
      status as InspectionSecurityStatusEnum
    ) &&
    documentObj?.isExpired
  ) {
    return true;
  }
  return false;
}

export function shouldHideCriteriaNotMetRuleTag(
  ruleStatus: string,
  inspectionStatus: InspectionStatusEnum
): boolean {
  return (
    ruleStatus ===
      INSPECTION_SECURITY_CHECK_LABEL_STATUS[
        InspectionSecurityStatusEnum.criteriaNotMet
      ] &&
    ![
      InspectionStatusEnum.RULE_BLOCKED,
      InspectionStatusEnum.APPROVAL_REQUIRED
    ].includes(inspectionStatus)
  );
}

function getVariantClassName(
  variant: string,
  variants: typeof Variants
): string {
  return variant === 'hidden' ? 'hidden' : (variants[variant as keyof typeof variants] ?? '');
}

export type SecurityCheckVariant =
  | 'success_light'
  | 'none_light'
  | 'info_light'
  | 'warning_light'
  | 'error_light';
interface ExtendedDocProps extends IDocProps {
  failedRules?: any[];
}

const SecurityCheck = ({
  docType,
  data,
  activeStep,
  failedRules
}: ExtendedDocProps) => {
  const {
    setActivePanel,
    setCollapse,
    inspectionDetailData,
    setActivity,
    setRightBarStatus,
    setVariant,
    setStatusChipLabel,
    setShowCommentHistory
  } = useInspectionDetailStore();
  const securityCheckList = SECURITY_CHECK_LIST[parseInt(activeStep) - 1];

  // Check if this is a passport inspection
  const isPassportInspection =
    inspectionDetailData?.inspectionChecks?.licenseEdits?.documentType ===
      'PASSPORT' ||
    inspectionDetailData?.inspectionChecks?.licenseAuthenticity
      ?.documentType === 'PASSPORT';

  const visibilityContext = {
    documentTypes: inspectionDetailData.documentTypes,
    assetType: inspectionDetailData.assetType,
    inspectionChecks: inspectionDetailData.inspectionChecks,
    stepValue: activeStep
  };

  const handleClick = (
    status: any,
    key: string,
    type: string,
    documentObj: any,
    isNotMatch: boolean
  ) => {
    if (isStatusUnavailableOrNotPerformed(status)) {
      return;
    }
    openSecurityCheckDetail(
      status,
      key,
      type,
      documentObj,
      isNotMatch,
      activeStep,
      {
        setCollapse,
        setActivePanel,
        setActivity,
        setRightBarStatus,
        setVariant,
        setStatusChipLabel,
        setShowCommentHistory
      },
      registrationPaperStatus
    );
  };
  // Helper function to get rule status for a specific check type.
  // Returns { label, isRuleError } when a matching failed rule is found, or null.
  // isRuleError is true when the rule is BLOCKED or APPROVAL_REQUIRED (row should show error colour).
  const getRuleStatus = (
    checkType: string,
    condition: string
  ): { label: InspectionStatusLabelEnum; isRuleError: boolean } | null => {
    if (!failedRules || failedRules.length === 0) return null;

    const ruleType = RULE_TYPE_MAP[checkType]?.[condition];
    if (!ruleType) return null;

    const matchingRule = failedRules.find((rule) => rule.ruleType === ruleType);
    if (!matchingRule) return null;

    if (matchingRule.exception === 'EXCEPTION_GRANTED') {
      return { label: InspectionStatusLabelEnum.Approved, isRuleError: false };
    }
    if (matchingRule.exception === 'EXCEPTION_REQUESTED') {
      // Simple approval flow – exception requested; inspection is not blocked.
      return { label: InspectionStatusLabelEnum.CriteriaNotMet, isRuleError: false };
    }
    if (
      matchingRule.exception === 'APPROVAL_REQUIRED' ||
      matchingRule.status === 'IN_REVIEW'
    ) {
      return { label: InspectionStatusLabelEnum.InReview, isRuleError: true };
    }
    // BLOCKED or unknown – rule is actively preventing submission.
    return { label: InspectionStatusLabelEnum.CriteriaNotMet, isRuleError: true };
  };

  /** Maps a photo rule's exception/status to { label, isRuleError }. */
  const getPhotoRuleDisplayStatus = (rule: {
    exception?: string;
    status?: string;
  }): { label: InspectionStatusLabelEnum; isRuleError: boolean } => {
    if (rule.exception === 'EXCEPTION_GRANTED') {
      return { label: InspectionStatusLabelEnum.Approved, isRuleError: false };
    }
    if (rule.exception === 'EXCEPTION_REQUESTED') {
      return { label: InspectionStatusLabelEnum.CriteriaNotMet, isRuleError: false };
    }
    if (
      rule.exception === 'APPROVAL_REQUIRED' ||
      rule.status === 'IN_REVIEW'
    ) {
      return { label: InspectionStatusLabelEnum.InReview, isRuleError: true };
    }
    return { label: InspectionStatusLabelEnum.CriteriaNotMet, isRuleError: true };
  };

  const registrationPaperStatus = !location.pathname.includes(
    '/demo/inspection'
  )
    ? inspectionDetailData.files.filter(
        (item: any) =>
          item.documentType === 'REGO_PAPERS' ||
          item.documentType === 'MARINE_REGO_CERT'
      ).length > 0
      ? true
      : false
    : false;

  // Helper function to check if key is in excluded list
  const isExcludedKey = (key: string) => {
    const excludedKeys = [
      'rav',
      'ppsr',
      'registration',
      'vehiclePhotos',
      'vehiclePhotosQuality',
      'vehiclePhotosAuthenticity',
      'dataEdits',
      'stolen',
      'writtenOff',
      'licenseExpiry',
      'licenseEdits',
      'licenseFaceMatch',
      'licenseLiveness',
      'licenseAuthenticity',
      'plate',
      'vin',
      'expiry',
      'registrationAuthenticity',
      'bankAuthenticity',
      'regoCertificate',
      'bankStatement',
      'BANK_STATEMENT_ADDRESS'
    ];
    return excludedKeys.includes(key);
  };

  // Helper function to check if status is in excluded list
  const isExcludedStatus = (status: any) => {
    return [
      InspectionSecurityStatusEnum.notPerformed,
      InspectionSecurityStatusEnum.notAvailable,
      InspectionSecurityStatusEnum.error
    ].includes(status);
  };

  // Helper function to determine if label should be shown
  const shouldShowLabel = (key: string, status: string) => {
    // Special case for ppsr and rav
    if (
      (key === 'ppsr' || key === 'rav') &&
      status === InspectionSecurityStatusEnum.notPerformed
    ) {
      return true;
    }

    // Original conditions
    return (
      !isExcludedKey(key) ||
      !isExcludedStatus(status) ||
      (status !== InspectionSecurityStatusEnum.notPerformed &&
        key === 'licenseExpiry')
    );
  };

  return (
    <>
      <div className="text-md">
        <h1 className="text-black font-semibold">
          {activeStep === '1'
            ? 'Security Check'
            : activeStep === '2'
              ? getAssetSpecificContent(
                  inspectionDetailData.assetType as AssetType
                ).title + ' Check'
              : activeStep === '3'
                ? getAssetSpecificContent(
                    inspectionDetailData.assetType as AssetType
                  ).title + ' / Registration Verification'
                : 'Bank Statement Checks'}
        </h1>
      </div>
      <div className="border-grey-200  w-full flex flex-col justify-center h-min">
        {securityCheckList.map((key: any, index) => {
          // For backward compatibility, convert old vehiclePhotos key to the new split keys
          if (key === 'vehiclePhotos' && activeStep === '2') {
            return;
          }

          const items: { [key: string]: any } =
            inspectionDetailData.inspectionChecks;

          if (!isVisibleSecurityCheck(key, visibilityContext)) {
            return;
          }

          let documentObj = getInspectionCheckDocument(
            items,
            key,
            activeStep,
            inspectionDetailData.assetType
          );

          // Fallback object for NEVDIS-only inspections when documentObj is not yet populated
          if (
            !documentObj &&
            activeStep === '2' &&
            inspectionDetailData.documentTypes.includes('NEVDIS') &&
            !SECURITY_CHECK_PHOTO_DOCUMENT_TYPES.some((type) =>
              inspectionDetailData.documentTypes.includes(type)
            )
          ) {
            documentObj = {
              status: InspectionSecurityStatusEnum.notRequested,
              type: key.toUpperCase()
            };
          }

          let type = documentObj && documentObj['type'];
          const isNotMatch =
            activeStep === '2' &&
            key === 'vin' &&
            [
              items?.vin?.licencePlateMatch,
              items?.licencePlate?.vinMatch
            ].includes('DIFFERENT');
          let status =
            resolveSecurityCheckStatus(
              documentObj,
              type,
              items,
              key,
              isNotMatch
            ) ?? InspectionSecurityStatusEnum.notPerformed;

          if (key === 'dataEdits') {
            // Prefer VIN; fall back to plate so plate-only templates still render.
            documentObj = getInspectionCheckDocument(
              items,
              'dataEdits',
              activeStep,
              inspectionDetailData.assetType
            );
            status = resolveVehicleDataEditsStatus(items);
            type = 'DATA_EDITS';
          }

          if (key === 'regoCertificate') {
            documentObj = items?.regoCertificate;
            type = REGO_CERTIFICATE_DATA_EDIT_TYPE;
            status =
              resolveSecurityCheckStatus(documentObj, type, items, key, false) ??
              InspectionSecurityStatusEnum.notPerformed;
          }
          if (key === 'bankStatement') {
            documentObj = items?.bankStatement;
            type = BANK_STATEMENT_DATA_EDIT_TYPE;
            status =
              resolveSecurityCheckStatus(documentObj, type, items, key, false) ??
              InspectionSecurityStatusEnum.notPerformed;
          }

          if (!documentObj) return;
          let label: string = docType[key as keyof typeof docType];

          // Override labels for passport inspection
          if (isPassportInspection && activeStep === '1') {
            if (key === 'licenseExpiry') {
              label = 'Passport Expiry';
            }
          }

          const getRuleData = (
            type: string,
            key: string
          ): {
            isApproved: boolean;
            isInReview: boolean;
            ruleStatus: InspectionStatusLabelEnum | null;
            photoRule?: any;
            isRuleError: boolean;
          } => {
            if (
              (key === 'vehiclePhotos' ||
                key === 'vehiclePhotosQuality' ||
                key === 'vehiclePhotosAuthenticity') &&
              failedRules?.length > 0
            ) {
              let photoRule;
              if (key === 'vehiclePhotosQuality') {
                photoRule = failedRules.find(
                  (rule) => rule.ruleType === 'ASSET_PHOTOS_QUALITY'
                );
              } else if (key === 'vehiclePhotosAuthenticity') {
                photoRule = failedRules.find(
                  (rule) => rule.ruleType === 'ASSET_PHOTOS_AUTHENTICITY'
                );
              } else {
                photoRule = failedRules.find(
                  (rule) =>
                    rule.ruleType === 'ASSET_PHOTOS_AUTHENTICITY' ||
                    rule.ruleType === 'ASSET_PHOTOS_QUALITY'
                );
              }

              if (photoRule) {
                const { label: ruleStatus, isRuleError } =
                  getPhotoRuleDisplayStatus(photoRule);
                const isApproved = ruleStatus === InspectionStatusLabelEnum.Approved;
                const isInReview = ruleStatus === InspectionStatusLabelEnum.InReview;
                return { isApproved, isInReview, ruleStatus, photoRule, isRuleError };
              }
            }
            const mapping = securityCheckTypeMappings[type];
            if (mapping) {
              const result = getRuleStatus(mapping.checkType, mapping.condition);
              if (result) {
                const { label: ruleStatus, isRuleError } = result;
                const isApproved = ruleStatus === InspectionStatusLabelEnum.Approved;
                const isInReview = ruleStatus === InspectionStatusLabelEnum.InReview;
                return { isApproved, isInReview, ruleStatus, isRuleError };
              }
            }
            const matchingRule = findMatchingFailedRule(failedRules, type, key);
            if (matchingRule) {
              const { label: ruleStatus, isRuleError } =
                getPhotoRuleDisplayStatus(matchingRule);
              return {
                isApproved: ruleStatus === InspectionStatusLabelEnum.Approved,
                isInReview: ruleStatus === InspectionStatusLabelEnum.InReview,
                ruleStatus,
                isRuleError
              };
            }
            return { isApproved: false, isInReview: false, ruleStatus: null, isRuleError: false };
          };

          const { isApproved, isInReview, ruleStatus, photoRule, isRuleError } = getRuleData(type, key);

          const variant = getVariant(
            status,
            type,
            documentObj,
            activeStep,
            registrationPaperStatus
          );
          const displayVariant =
            key === 'vin' &&
            status === InspectionSecurityStatusEnum.discrepancyFound
              ? 'error_light'
              : variant;
          // Non-photo BLOCKED / APPROVAL_REQUIRED rules force error colour.
          // AI photo quality/authenticity keep the check status (Warning → orange),
          // matching development: Criteria Not Met is a separate tag, not a red row.
          const isPpsrRequestedException =
            (type === 'PPSR_ENCUMBRANCE' ||
              type === 'MARINE_PPSR_ENCUMBRANCE') &&
            status === InspectionSecurityStatusEnum.reportFound &&
            !isRuleError &&
            ruleStatus === InspectionStatusLabelEnum.CriteriaNotMet;
          const effectiveVariant =
            !isApproved &&
            isRuleError &&
            !isAiPhotoSecurityCheck(type, key)
              ? 'error_light'
              : isPpsrRequestedException
                ? 'warning_light'
                : displayVariant;
          const variantClass = getVariantClassName(effectiveVariant, Variants);
          const isClickable = isRowClickable(status, key, type, documentObj);
          const isHiddenByNotRequested =
            status === InspectionSecurityStatusEnum.notRequested &&
            key !== 'ppsr' &&
            key !== 'rav';

          return (
            <div
              className={clsx(
                'flex border rounded-xl px-3.5 py-3 items-center justify-between mt-2 whitespace-nowrap flex-wrap items-start gap-y-[6px]',
                isClickable && 'cursor-pointer',
                index === 0 && 'first:mt-0 cursor-default',
                isApproved && Variants.success_light,
                !isApproved && variantClass,
                isHiddenByNotRequested && 'hidden'
              )}
              key={index}
              onClick={() =>
                isClickable && handleClick(status, key, type, documentObj, isNotMatch)
              }>
              <div className="flex flex-row items-center whitespace-nowrap gap-3">
                {(key === 'vehiclePhotos' ||
                  key === 'vehiclePhotosQuality' ||
                  key === 'vehiclePhotosAuthenticity') && (
                  <span className="text-white bg-[#033E73] rounded-[4px] px-2 py-[1px] text-sm ">
                    AI
                  </span>
                )}
                <span className="text-sm font-semibold">{label}</span>
              </div>
              {shouldShowLabel(key, status) && (
                <div className="flex flex-wrap flex-row justify-start gap-y-[6px] gap-x-6 whitespace-nowrap">
                  {shouldShowDetailsLink(status, key, type, documentObj) && (
                    <span className="flex flex-row font-semibold justify-center items-center text-sm text-grey-600">
                      <span className="underline">Details</span>
                      <ArrowIcon />
                    </span>
                  )}
                  {!isApproved && (
                    <StatusChip
                      variant={
                        effectiveVariant === 'hidden'
                          ? 'info_light'
                          : (effectiveVariant as React.ComponentProps<
                              typeof StatusChip
                            >['variant'])
                      }
                      clsName={clsx('border text-xs font-normal max-w-full')}
                      label={getLabel(
                        status,
                        type,
                        documentObj,
                        isNotMatch,
                        activeStep,
                        registrationPaperStatus
                      )}
                    />
                  )}
                  {/* Add rule status tag if available */}
                  {((isInReview: boolean, ruleStatus: string, isApproved: boolean, photoRule: string) => {
                    // Special handling for vehiclePhotos - split into quality and authenticity
                    if (
                      (key === 'vehiclePhotos' ||
                        key === 'vehiclePhotosQuality' ||
                        key === 'vehiclePhotosAuthenticity') &&
                      failedRules &&
                      failedRules.length > 0 &&
                      photoRule
                    ) {
                      if (
                        !isApproved &&
                        !isInReview &&
                        inspectionDetailData.status !== InspectionStatusEnum.RULE_BLOCKED &&
                        inspectionDetailData.status !== InspectionStatusEnum.APPROVAL_REQUIRED
                      ) {
                        return null;
                      }
                      return (
                        <span
                          className={clsx(
                            'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap',
                            isInReview
                              ? 'bg-secondary-light/10 border border-secondary-light text-secondary-light'
                              : isApproved
                                ? 'bg-success text-white'
                                : 'bg-error-50 text-error-700 border border-error-300'
                          )}>
                          {ruleStatus}
                        </span>
                      );
                    }

                    if (!ruleStatus) return null;

                    // If the rule status is 'Criteria Not Met'
                    // and the inspection is not in a blocking/approval state
                    // then don't show the tag
                    const shouldHideCriteriaNotMetTag =
                      shouldHideCriteriaNotMetRuleTag(
                        ruleStatus,
                        inspectionDetailData.status
                      );
                    if (shouldHideCriteriaNotMetTag) {
                      return null;
                    }
                    return (
                      <span
                        className={clsx(
                          'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap',
                          isInReview
                            ? 'bg-secondary-light/10 border border-secondary-light text-secondary-light'
                            : isApproved
                              ? 'bg-success-600 text-white'
                              : 'bg-error-50 text-error-700 border border-error-300'
                        )}>
                        {ruleStatus}
                      </span>
                    );
                  })(isInReview, ruleStatus, isApproved, photoRule)}
                </div>
              )}
            </div>
          );
        })}
        <div></div>
      </div>
    </>
  );
};

export default SecurityCheck;
